<template>
  <section class="mt-12 w-full">
    <!-- Location tabs for groups route -->
    <LazyMarketingNavigationLocationsTabs
      v-if="showLocationTabs && groupedActivities.length > 0"
      :blok="blok"
      :uuid="uuid"
      :type="type"
      :grouped-products="groupedActivities"
      :selected-location="selectedLocation"
      @update:selected-location="selectedLocation = $event"
      @location-changed="updateLocation"
    />

    <div v-if="packages.length === 0" class="mt-4">
      <div
        v-if="!isLoading && filteredActivities.length === 0"
        class="text-center text-neutral-400"
      >
        There are currently no activities available at {{ blok.headline }}.
      </div>

      <div class="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
        <LazyMarketingCardActivity
          v-for="(activity, index) in sortedActivities"
          v-if="!isLoading && filteredActivities.length > 0"
          :key="index"
          :activity="activity"
          :blok="blok"
          :type="type"
          :name="name"
        />
        <LazyMarketingCardActivityLoading
          v-for="i in 4"
          v-if="isLoading"
          :key="i"
        />
      </div>
    </div>

    <UTabs
      v-else
      v-model="activeTab"
      :items="activityNav"
      :unmount-on-hide="false"
      :ui="{
        list: 'bg-neutral-900/60 border border-neutral-800 backdrop-blur-lg overflow-hidden rounded-2xl',
        trigger: 'disabled:!text-neutral-600 disabled:text-xs',
        label: 'text-xs sm:text-sm font-extrabold truncate',
        content:
          'space-y-8 mt-4 grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4',
      }"
      @update:model-value="updateRouteParams"
    >
      <template #activities>
        <LazyMarketingCardActivity
          v-for="(activity, index) in sortedActivities"
          v-if="!isLoading && filteredActivities.length > 0"
          :key="index"
          :activity="activity"
          :blok="blok"
          :type="type"
          :name="name"
        />
        <LazyMarketingCardActivityLoading
          v-for="i in 4"
          v-if="isLoading"
          :key="i"
        />
      </template>

      <template #packages>
        <LazyMarketingCardActivity
          v-for="(activity, index) in packages.sort((a, b) => {
            const typeOrder =
              Number(b.content.activity_type === 'corporates') -
              Number(a.content.activity_type === 'corporates');
            if (typeOrder !== 0) return typeOrder;

            const priceA = a.content.products[0]?.price ?? 0;
            const priceB = b.content.products[0]?.price ?? 0;
            const priceOrder = priceB - priceA;
            if (priceOrder !== 0) return priceOrder;

            return a.name.localeCompare(b.name);
          })"
          v-if="!isLoading && activities.length > 0"
          :key="index"
          :activity="activity"
          :blok="blok"
          :name="name"
          :package-card="true"
        />
        <LazyMarketingCardActivityLoading
          v-for="i in 4"
          v-if="isLoading"
          :key="i"
        />
      </template>
    </UTabs>
  </section>
</template>

<script setup lang="ts">
import type {
  ActivityStoryblok,
  LocationStoryblok,
  PackageStoryblok,
} from '~/types/component-types-sb';

interface Props {
  blok?: Record<string, any>;
  uuid?: string;
  type?: string;
  name?: string;
}

const props = withDefaults(defineProps<Props>(), {
  blok: () => ({}),
  uuid: '',
  type: '',
  name: '',
});

const emit = defineEmits<{
  'activities-count': [count: number];
  'packages-count': [count: number];
}>();

const { fetchActivities, fetchLocations, fetchPackages, isLoading } =
  useStoryblokClient();
const { slugify } = useSlugify();
const route = useRoute();
const router = useRouter();

const activities = ref<ActivityStoryblok[]>([]);
const selectedLocation = ref(0);
const locations = ref<LocationStoryblok[]>([]);
const groupedActivities = ref<
  {
    locationName: string;
    locationTown: string;
    locationUUID: string;
    locationSlug: string;
    products: ActivityStoryblok[];
  }[]
>([]);
const packages = ref<PackageStoryblok[]>([]);

// Computed property to check if we should show location tabs
const showLocationTabs = computed(() => {
  return route.path.includes('/groups');
});

// Computed property for filtered activities
const filteredActivities = computed(() => {
  if (showLocationTabs.value && groupedActivities.value.length > 0) {
    return groupedActivities.value[selectedLocation.value]?.products || [];
  }
  return activities.value;
});

// Computed property for sorted activities
const sortedActivities = computed(() => {
  return [...filteredActivities.value].sort((a, b) => {
    const priceA = a.content?.products?.[0]?.price ?? 0;
    const priceB = b.content?.products?.[0]?.price ?? 0;
    const priceOrder = Number(priceB) - Number(priceA);
    if (priceOrder !== 0) return priceOrder;

    return (a.name ?? '').localeCompare(b.name ?? '');
  });
});

// Group activities by location
const groupActivitiesByLocation = (
  activities: ActivityStoryblok[],
  locations: LocationStoryblok[]
) => {
  // Create the "All Locations" group first
  const allLocationsGroup = {
    locationName: 'All Locations',
    locationTown: 'All',
    locationUUID: 'all',
    locationSlug: 'all',
    products: activities,
  };

  // Create groups for individual locations
  const locationGroups = locations
    .map((location) => ({
      locationName: location.name,
      locationTown: location.content?.town,
      locationUUID: location.uuid,
      locationSlug: location.slug,
      products: activities.filter((activity) =>
        activity.content?.locations?.some(
          (loc: LocationStoryblok) => loc.uuid === location.uuid
        )
      ),
    }))
    .filter((group) => group.products.length > 0)
    .sort((a, b) => a.locationName.localeCompare(b.locationName));

  // Return array with "All Locations" first, followed by individual locations
  if (allLocationsGroup.products.length > 2) {
    return [allLocationsGroup, ...locationGroups];
  } else {
    return locationGroups;
  }
};

// Update location handler
const updateLocation = (index: number) => {
  selectedLocation.value = index;

  const locationName =
    index === 0 ? 'all' : slugify(groupedActivities.value[index].locationName);

  router.replace({
    query: {
      ...route.query,
      location: locationName,
    },
  });
};

// Load activities and set them in the reactive `activities` array
const loadActivities = async () => {
  try {
    const [stories, locationData, packageData] = await Promise.all([
      fetchActivities(),
      fetchLocations(),
      fetchPackages(),
    ]);

    locations.value = locationData;
    packages.value = packageData.filter((activity) =>
      activity.content?.locations?.some(
        (location: LocationStoryblok) =>
          location.content && location.uuid === props.uuid
      )
    );

    // Filter activities based on type
    if (props.type === 'location') {
      activities.value = stories
        .filter((activity) =>
          activity.content?.locations?.some(
            (location: LocationStoryblok) =>
              location.content && location.uuid === props.uuid
          )
        )
        .filter((story) => story.content?.activity_type !== 'corporates');
    } else if (props.type === 'event') {
      activities.value = stories.filter((story) =>
        props.blok.activities?.includes(story.uuid)
      );
    } else if (props.type === 'group') {
      let filteredActivities = stories.filter((activities) =>
        props.blok.activities?.some(
          (activity: string) => activity === activities.uuid
        )
      );

      if (!route.path.includes('corporate')) {
        filteredActivities = filteredActivities.filter(
          (story) => story.content?.activity_type !== 'corporates'
        );
      }

      activities.value = filteredActivities;

      // If we're on the groups route, set up the grouped activities
      if (showLocationTabs.value) {
        groupedActivities.value = groupActivitiesByLocation(
          activities.value,
          locations.value
        );
      }
    } else {
      activities.value = stories;
    }

    if (activities.value) {
      emit('activities-count', activities.value.length);
    }

    if (packages.value) {
      emit('packages-count', packages.value.length);
    }
  } catch (error) {
    console.error(error);
  }
};

// Check for location in URL on mount
onMounted(() => {
  const locationParam = route.query.location;
  if (!locationParam || locationParam === 'all') {
    selectedLocation.value = 0; // Default to "All Locations"
  } else if (groupedActivities.value.length > 0) {
    const foundIndex = groupedActivities.value.findIndex(
      (location) => slugify(location.locationName) === locationParam
    );
    if (foundIndex !== -1) {
      selectedLocation.value = foundIndex;
    }
  }
});

// Watch for query parameter changes
watch(
  () => route.query.location,
  (newLocationQuery) => {
    if (!newLocationQuery || newLocationQuery === 'all') {
      selectedLocation.value = 0; // Set to "All Locations"
    } else if (groupedActivities.value.length > 0) {
      const foundIndex = groupedActivities.value.findIndex(
        (location) => slugify(location.locationName) === newLocationQuery
      );
      if (foundIndex !== -1) {
        selectedLocation.value = foundIndex;
      }
    }
  }
);

await loadActivities();

const activeTab = ref('0');

const activityNav = ref([
  {
    label: 'Activities',
    slot: 'activities',
    disabled: false,
  },
  {
    label: 'Packages',
    slot: 'packages',
    disabled: false,
  },
]);

const updateRouteParams = (value: string | number) => {
  router.replace({
    query: {
      ...route.query,
      tab: String(value),
    },
  });
};
</script>
